import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { supabase } from '../supabaseClient';
import { 
    Clock, Shield, CheckCircle, AlertTriangle, FileText, Upload, 
    ArrowRight, Star, DollarSign, Calendar, RefreshCw, Loader, Eye 
} from 'lucide-react';

const ProviderDashboard = () => {
    const { userId, isSignedIn, isLoaded } = useAuth();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [provider, setProvider] = useState(null);
    const [stats, setStats] = useState({ earnings: 0, rating: 5.0, bookingsCount: 0 });
    
    // File Upload State
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const fetchProviderData = async () => {
        if (!userId) return;
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('providers')
                .select('*')
                .eq('id', userId)
                .maybeSingle();

            if (error) throw error;
            setProvider(data);

            if (data && (data.status === 'live' || data.status === 'approved')) {
                const { data: wallet } = await supabase
                    .from('wallets')
                    .select('balance')
                    .eq('id', userId)
                    .maybeSingle();

                const { count: bookingsCount } = await supabase
                    .from('bookings')
                    .select('*', { count: 'exact', head: true })
                    .eq('provider_id', userId);

                setStats({
                    earnings: wallet ? parseFloat(wallet.balance) : 0,
                    rating: 5.0,
                    bookingsCount: bookingsCount || 0
                });
            }
        } catch (err) {
            console.error('[ProviderDashboard] Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isLoaded && isSignedIn) {
            fetchProviderData();
        } else if (isLoaded && !isSignedIn) {
            setLoading(false);
        }
    }, [isLoaded, isSignedIn, userId]);

    // Handle File Selector Trigger
    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle selected file change
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setUploadError("File size exceeds the 5MB limit.");
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
            setUploadError(null);
            setUploadSuccess(false);
        }
    };

    // Upload to Supabase Storage and Update status
    const handleUploadSubmit = async () => {
        if (!selectedFile) {
            setUploadError("Please select a file to upload first.");
            return;
        }

        try {
            setUploading(true);
            setUploadError(null);

            const fileExt = selectedFile.name.split('.').pop();
            const fileName = `${Date.now()}.${fileExt}`;
            const filePath = `${userId}/${fileName}`;

            console.log(`[Dashboard Upload] Uploading ${selectedFile.name} to storage bucket...`);

            // 1. Upload to Supabase Storage (under bucket provider-documents)
            const { data: storageData, error: storageErr } = await supabase.storage
                .from('provider-documents')
                .upload(filePath, selectedFile, {
                    cacheControl: '3600',
                    upsert: true
                });

            if (storageErr) throw storageErr;

            // Get public URL
            const { data: urlData } = supabase.storage
                .from('provider-documents')
                .getPublicUrl(filePath);

            const publicUrl = urlData.publicUrl;

            // 2. Insert into public.provider_documents table
            const { error: dbErr } = await supabase
                .from('provider_documents')
                .insert({
                    provider_id: userId,
                    document_type: 'onboarding_verification',
                    file_url: publicUrl,
                    status: 'pending'
                });

            if (dbErr) throw dbErr;

            // 3. Update providers application status back to 'under_review'
            const { error: providerErr } = await supabase
                .from('providers')
                .update({ status: 'under_review' })
                .eq('id', userId);

            if (providerErr) throw providerErr;

            setUploadSuccess(true);
            setSelectedFile(null);
            console.log("[Dashboard Upload] Document submitted successfully!");
            
            // Refresh dashboard data to show 'Under Review' status
            fetchProviderData();
        } catch (err) {
            console.error('[Dashboard Upload] Error uploading document:', err);
            setUploadError(err.message || "Failed to upload document. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    if (!isLoaded || loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column', gap: '16px' }}>
                <Loader className="animate-spin" size={40} color="var(--primary-green)" style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-light)' }}>Loading your dashboard...</span>
            </div>
        );
    }

    if (!isSignedIn) {
        return (
            <div style={{ padding: '40px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', background: 'white', borderRadius: '16px', margin: '20px' }}>
                <Shield size={64} color="var(--primary-green)" />
                <h2 style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-dark)' }}>Provider Center</h2>
                <p style={{ fontSize: '14px', color: 'var(--text-light)', maxWidth: '300px', lineHeight: '1.6' }}>
                    Log in to register your services, check your application status, or manage your provider bookings.
                </p>
                <button onClick={() => navigate('/')} style={{ background: 'var(--primary-green)', color: 'white', border: 'none', padding: '12px 28px', borderRadius: '30px', fontWeight: '700', cursor: 'pointer' }}>
                    Go to Homepage
                </button>
            </div>
        );
    }

    // CASE 1: Signed in but no provider record found
    if (!provider) {
        return (
            <div style={{ padding: '30px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ background: 'linear-gradient(135deg, #006838 0%, #16a34a 100%)', padding: '30px 20px', borderRadius: '24px', color: 'white', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                    <TrendingUp size={48} />
                    <h2 style={{ fontSize: '24px', fontWeight: '800', margin: 0 }}>Earn with EZ Naija</h2>
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', maxWidth: '320px', lineHeight: '1.6', margin: 0 }}>
                        Offer airport pickups, private dining experiences, or medical consultations to visitors in Lagos.
                    </p>
                    <button onClick={() => navigate('/provider-setup')} style={{ background: 'white', color: 'var(--primary-green)', border: 'none', padding: '14px 28px', borderRadius: '30px', fontWeight: '800', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}>
                        Start Onboarding <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        );
    }

    // CASE 2: Under Review / Submitted status
    if (provider.status === 'submitted' || provider.status === 'under_review') {
        return (
            <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h2 style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-dark)' }}>Application Status</h2>
                        <span style={{ fontSize: '13px', color: 'var(--text-light)', fontWeight: '600' }}>ID: {provider.application_id}</span>
                    </div>
                    <button onClick={fetchProviderData} style={{ background: '#f3f4f6', border: 'none', padding: '8px', borderRadius: '50%', cursor: 'pointer', color: 'var(--text-dark)' }}>
                        <RefreshCw size={16} />
                    </button>
                </div>

                <div style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: '20px', padding: '24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px' }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
                        <Clock size={32} />
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#111827', margin: 0 }}>Under Review</h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-light)', lineHeight: '1.6', margin: 0, maxWidth: '280px' }}>
                        Our compliance team is verifying your registration. Status checks complete within 24 to 48 hours.
                    </p>
                </div>

                {/* Timeline display */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '10px' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CheckCircle size={20} color="var(--primary-green)" />
                            <div style={{ width: '2px', height: '40px', background: 'var(--primary-green)' }}></div>
                        </div>
                        <div>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', margin: '0 0 4px' }}>Submission Received</h4>
                            <p style={{ fontSize: '12px', color: 'var(--text-light)', margin: 0 }}>Details written successfully to registration queue.</p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Clock size={20} color="#d97706" />
                            <div style={{ width: '2px', height: '40px', background: '#e5e7eb' }}></div>
                        </div>
                        <div>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#d97706', margin: '0 0 4px' }}>Credentials Verification</h4>
                            <p style={{ fontSize: '12px', color: 'var(--text-light)', margin: 0 }}>Checking qualifications and ID details.</p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Shield size={20} color="#9ca3af" />
                        </div>
                        <div>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#9ca3af', margin: '0 0 4px' }}>Marketplace Publication</h4>
                            <p style={{ fontSize: '12px', color: 'var(--text-light)', margin: 0 }}>Approval and listing on the live discovery app.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // CASE 3: Awaiting Documents status
    if (provider.status === 'awaiting_documents') {
        return (
            <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '20px', padding: '20px', display: 'flex', gap: '16px' }}>
                    <AlertTriangle color="#dc2626" size={24} style={{ flexShrink: 0 }} />
                    <div>
                        <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#991b1b', margin: '0 0 4px' }}>Action Required: Missing Documents</h3>
                        <p style={{ fontSize: '13px', color: '#b91c1c', margin: 0, lineHeight: '1.5' }}>
                            Our vetting team requires additional file uploads to complete your verification checks.
                        </p>
                    </div>
                </div>

                <div style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: '20px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', margin: 0 }}>Upload Verification Documents</h4>
                    
                    {/* Hidden Native File Input */}
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        accept="image/*,.pdf" 
                        onChange={handleFileChange} 
                    />

                    {/* Interactive Dropzone */}
                    <div 
                        onClick={triggerFileSelect}
                        style={{ 
                            border: selectedFile ? '2.5px solid var(--primary-green)' : '2px dashed var(--border-gray)', 
                            borderRadius: '12px', 
                            padding: '24px', 
                            textAlign: 'center', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: '10px', 
                            cursor: 'pointer',
                            background: selectedFile ? 'var(--light-green)' : '#f9fafb',
                            transition: '0.2s'
                        }}
                    >
                        <Upload size={24} color={selectedFile ? 'var(--primary-green)' : 'var(--text-light)'} />
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-dark)' }}>
                            {selectedFile ? 'Change Selected File' : 'Select or Drag file here'}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--text-light)', fontStyle: selectedFile ? 'normal' : 'italic' }}>
                            {selectedFile ? `Selected: ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)` : 'Supports PDF, JPG, PNG up to 5MB'}
                        </span>
                    </div>

                    {uploadError && (
                        <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600', padding: '0 4px' }}>
                            Error: {uploadError}
                        </div>
                    )}

                    {uploadSuccess && (
                        <div style={{ fontSize: '12px', color: 'var(--primary-green)', fontWeight: '600', padding: '0 4px' }}>
                            Document submitted successfully! Reviewing...
                        </div>
                    )}

                    <button 
                        onClick={handleUploadSubmit}
                        disabled={uploading || !selectedFile}
                        style={{ 
                            background: uploading || !selectedFile ? '#9ca3af' : 'var(--primary-green)', 
                            color: 'white', 
                            border: 'none', 
                            width: '100%', 
                            padding: '14px', 
                            borderRadius: '12px', 
                            fontWeight: '700', 
                            fontSize: '14px', 
                            cursor: uploading || !selectedFile ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '8px',
                            transition: '0.2s'
                        }}
                    >
                        {uploading ? (
                            <>
                                <Loader className="animate-spin" size={16} />
                                Uploading Document...
                            </>
                        ) : 'Submit Document'}
                    </button>
                </div>
            </div>
        );
    }

    // CASE 4: Live / Approved status
    return (
        <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-dark)' }}>Provider Dashboard</h2>
                    <span style={{ fontSize: '13px', color: 'var(--primary-green)', fontWeight: '700', background: 'var(--light-green)', padding: '4px 8px', borderRadius: '12px' }}>LIVE ON MARKETPLACE</span>
                </div>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#e5e7eb', overflow: 'hidden' }}>
                    <img src={`https://avatar.vercel.sh/${provider.full_name}`} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
            </div>

            {/* Quick Metrics Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ background: 'white', border: '1px solid var(--border-gray)', padding: '16px', borderRadius: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-light)' }}>
                        <span style={{ fontSize: '11px', fontWeight: '600' }}>WALLET BALANCE</span>
                        <DollarSign size={14} />
                    </div>
                    <span style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-dark)' }}>₦{stats.earnings.toLocaleString()}</span>
                </div>

                <div style={{ background: 'white', border: '1px solid var(--border-gray)', padding: '16px', borderRadius: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-light)' }}>
                        <span style={{ fontSize: '11px', fontWeight: '600' }}>TOTAL BOOKINGS</span>
                        <Calendar size={14} />
                    </div>
                    <span style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-dark)' }}>{stats.bookingsCount}</span>
                </div>
            </div>

            {/* Performance Review Status */}
            <div style={{ background: 'white', border: '1px solid var(--border-gray)', padding: '16px 20px', borderRadius: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Star size={20} color="#eab308" fill="#eab308" />
                    <div>
                        <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', margin: 0 }}>Provider Rating</h4>
                        <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>Based on customer feedback</span>
                    </div>
                </div>
                <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-dark)' }}>{stats.rating.toFixed(1)}</span>
            </div>

            {/* Active Bookings Placeholder */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-dark)', margin: 0 }}>Recent Activities</h3>
                <div style={{ background: '#f9fafb', border: '1px dashed var(--border-gray)', padding: '30px 20px', borderRadius: '20px', textAlign: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-light)', fontWeight: '600' }}>No active assignments currently.</span>
                </div>
            </div>
        </div>
    );
};

export default ProviderDashboard;
